import { uiText } from '../i18n/uiText';
import React from 'react';
import { Link } from 'react-router-dom';
import {
  Building2,
  CheckSquare,
  ClipboardList,
  FileText,
  FolderKanban,
  KeyRound,
  ShieldCheck,
  ServerCog,
  Users,
} from 'lucide-react';
import PageIntro, { HelpText, QuickActions } from '../components/ui/PageIntro';
import Badge from '../components/ui/Badge';
import { useManagementWorkspace } from '../hooks/useData';
import { useAuth } from '../store/AuthContext';

const BLOCKS = [
  { title: 'Библиотека требований', description: 'Подготовить аудит по деятельности компании или настроить правила для новых проверок.', to: '/requirements', icon: CheckSquare, classifierOnly: true },
  {
    title: uiText("Квалификационная оценка"),
    description: uiText("С чего начать: оценить организацию, проверить её в ФНС и получить подтверждённый профиль."),
    to: '/qualification',
    icon: ShieldCheck,
    qualificationOnly: true,
  },
  {
    title: uiText("Функции и экономика"),
    description: uiText("Найти исполнителей по функциям, собрать группу и рассчитать потенциальный эффект аудита."),
    to: '/qualification?tab=analysis',
    icon: FolderKanban,
    qualificationOnly: true,
  },
  {
    title: uiText("Задачи и сравнение"),
    description: uiText("Описать задачу, сравнить кандидатов с требованиями и зафиксировать решение."),
    to: '/qualification?tab=needs',
    icon: ClipboardList,
    qualificationOnly: true,
  },
  {
    title: uiText("Шаблоны чек-листов"),
    description: uiText("Вопросы, критерии, типы аудитов и базовая структура проверки."),
    to: '/templates',
    icon: CheckSquare,
  },
  {
    title: uiText("Компании"),
    description: uiText("Реестр организаций, сотрудники компаний и данные для назначения аудитов."),
    to: '/companies',
    icon: Building2,
  },
  {
    title: uiText("Учетные записи"),
    description: uiText("Пользователи, роли, логины и состояние первого входа."),
    to: '/users',
    icon: Users,
    adminOnly: true,
  },
  {
    title: uiText("Процесс аудита"),
    description: uiText("Этапы, контрольные точки, документы и правила завершения."),
    to: '/audits',
    icon: ShieldCheck,
  },
  {
    title: uiText("Контроль системы"),
    description: uiText("База, резервные копии, письма, очереди и целостность процессов."),
    to: '/admin/system-control',
    icon: ServerCog,
    adminOnly: true,
  },
];

const TEMPLATE_STATUS_LABELS = {
  draft: uiText("Черновик"),
  published: uiText("Опубликован"),
  archived: uiText("Архив"),
};

function accessBadge(user) {
  if (!user.is_active) return <Badge variant="danger">{uiText("Выключен")}</Badge>;
  if (!user.has_password) return <Badge variant="warning">{uiText("Ждет первый вход")}</Badge>;
  if (user.must_change_password) return <Badge variant="warning">{uiText("Смена пароля")}</Badge>;
  return <Badge variant="success">{uiText("Активен")}</Badge>;
}

export default function ManagementPage() {
  const { user } = useAuth();
  const isAdmin = user?.role === 'admin';
  const qualificationEnabled = process.env.REACT_APP_ENABLE_QUALIFICATION === 'true';
  const managementState = useManagementWorkspace();
  const data = managementState.data || {};
  const summary = data.summary || {};
  const team = data.highlights?.team_recent || [];
  const templates = data.highlights?.templates_recent || [];
  const visibleBlocks = BLOCKS.filter((block) => (!block.adminOnly || isAdmin)
    && (!block.qualificationOnly || qualificationEnabled) && (!block.classifierOnly || process.env.REACT_APP_ENABLE_CLASSIFIER === 'true'));

  return (
    <div className="workspace-page">
      <PageIntro
        eyebrow={uiText("Управление")}
        title={uiText("Управление системой")}
        icon={ShieldCheck}
        description={isAdmin
          ? uiText("Шаблоны, компании, команда и правила процесса.")
          : uiText("Справочники и документы для подготовки и ведения аудитов.")}
        aside={isAdmin ? (
          <div className="workspace-side-actions">
            <Link className="btn btn-outline" to="/users"><Users size={15} />{uiText(" Открыть учетные записи")}</Link>
          </div>
        ) : null}
      >
        <HelpText>{uiText("Выберите раздел для работы с компаниями, сотрудниками и документами.")}</HelpText>
        <QuickActions>
          {qualificationEnabled ? <Link className="btn btn-primary" to="/qualification"><ShieldCheck size={15} />{uiText(" Квалификационная оценка")}</Link> : null}
          {isAdmin ? <Link className="btn btn-outline" to="/users"><Users size={15} />{uiText(" Создание пользователей")}</Link> : null}
          {qualificationEnabled && isAdmin ? <Link className="btn btn-outline" to="/qualification?tab=grants"><KeyRound size={15} />{uiText(" Полномочия квалификации")}</Link> : null}
          <Link className="btn btn-outline" to="/audits"><FolderKanban size={15} />{uiText(" Список аудитов")}</Link>
        </QuickActions>
      </PageIntro>

      {managementState.error ? <div className="alert alert-error">{managementState.error}</div> : null}

      <div className="workspace-link-grid" style={{ marginBottom: 16 }}>
        {visibleBlocks.map((block) => {
          const Icon = block.icon;
          return (
            <Link key={block.to} to={block.to} className="workspace-link-card">
              <div className="workspace-link-icon"><Icon size={18} /></div>
              <div>
                <div className="workspace-link-title">{block.title}</div>
                <div className="workspace-muted">{block.description}</div>
              </div>
            </Link>
          );
        })}
      </div>

      <div className="workspace-grid-two">
        <div className="card">
          <div className="card-header">
            <div className="card-title">{uiText("Недавно обновленные шаблоны")}</div>
            <Badge variant="info">{templates.length}</Badge>
          </div>
          <div className="card-body workspace-list">
            {templates.length === 0 ? (
              <div className="workspace-empty-state">
                <div className="workspace-empty-state-title">{uiText("Шаблоны еще не добавлены")}</div>
                <div className="workspace-empty-state-text">{uiText("Создайте первый шаблон, чтобы команда работала по единой структуре проверки.")}</div>
                <Link className="btn btn-outline btn-sm" to="/templates">{uiText("Открыть шаблоны")}</Link>
              </div>
            ) : templates.map((item) => (
              <Link key={item.id} to="/templates" className="workspace-list-item">
                <div>
                  <div className="workspace-item-title">{item.name}</div>
                  <div className="workspace-muted">{uiText("Статус: ")}{TEMPLATE_STATUS_LABELS[item.status] || item.status || uiText("Черновик")}</div>
                </div>
                <Badge variant="neutral">{uiText("Шаблон")}</Badge>
              </Link>
            ))}
          </div>
        </div>

        {isAdmin ? (
          <div className="card">
            <div className="card-header">
              <div className="card-title">{uiText("Доступы и первый вход")}</div>
              <Badge variant={(summary.pending_first_login_count || 0) ? 'warning' : 'success'}>
                <KeyRound size={13} /> {summary.pending_first_login_count || 0}
              </Badge>
            </div>
            <div className="card-body workspace-list">
              {team.length === 0 ? (
                <div className="workspace-empty-state">
                  <div className="workspace-empty-state-title">{uiText("Команда пока не заполнена")}</div>
                  <div className="workspace-empty-state-text">{uiText("Добавьте пользователей, чтобы назначать аудиторов, проверяемых и владельцев процесса.")}</div>
                  <Link className="btn btn-outline btn-sm" to="/users">{uiText("Открыть команду")}</Link>
                </div>
              ) : team.map((item) => (
                <Link key={item.id} to="/users" className="workspace-list-item">
                  <div>
                    <div className="workspace-item-title">{item.full_name}</div>
                    <div className="workspace-muted">{item.username} · {item.email || item.role}</div>
                  </div>
                  {accessBadge(item)}
                </Link>
              ))}
            </div>
          </div>
        ) : (
          <div className="card">
            <div className="card-header">
              <div className="card-title">{uiText("Рабочие разделы аудитора")}</div>
              <Badge variant="neutral">{uiText("Доступно")}</Badge>
            </div>
            <div className="card-body workspace-list">
              <Link to="/companies" className="workspace-list-item">
                <div><div className="workspace-item-title">{uiText("Компании")}</div><div className="workspace-muted">{uiText("Контакты, адреса и сотрудники проверяемых организаций.")}</div></div>
                <Building2 size={17} />
              </Link>
              <Link to="/audit-programs" className="workspace-list-item">
                <div><div className="workspace-item-title">{uiText("Планы аудитов")}</div><div className="workspace-muted">{uiText("Формирование программы и почасового графика проверки.")}</div></div>
                <ClipboardList size={17} />
              </Link>
              <Link to="/audit-reports" className="workspace-list-item">
                <div><div className="workspace-item-title">{uiText("Отчёты по аудитам")}</div><div className="workspace-muted">{uiText("Подготовка итоговых документов по проведённым аудитам.")}</div></div>
                <FileText size={17} />
              </Link>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
